'use client'

import { motion } from 'framer-motion'
import { useState } from 'react'
import { RouteLink } from '@/lib/router'
import { MagneticLink } from './magnetic-button'
import { ThemeToggle } from './theme-toggle'
import logoHeader from '@/assets/logo-header.png'
import logoFooter from '@/assets/logo-footer.png'

const logoClass = 'block h-12 w-auto sm:h-14 md:h-16'

const navItems = [
  { label: 'Work', href: '/portfolio' },
  { label: 'Services', href: '/services' },
  { label: 'Blog', href: '/blog' },
  { label: 'About', href: '/#about' },
]

const navLinkClass =
  'inline-flex items-center leading-none text-xs tracking-[0.15em] uppercase text-neutral-600 hover:text-foreground transition-colors duration-300'

export function Navigation() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  return (
    <>
      <motion.header
        className="fixed top-0 left-0 right-0 z-50 isolate border-b border-border bg-background [transform:translateZ(0)] [backface-visibility:hidden]"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      >
        <nav className="relative flex w-full items-center justify-between px-6 py-3 lg:px-12 lg:py-3.5">
          {/* Logo */}
          <RouteLink to="/" className="inline-flex shrink-0 items-center">
            <img
              src={logoHeader}
              alt="Elementary Creative"
              className={`${logoClass} dark:hidden`}
            />
            <img
              src={logoFooter}
              alt="Elementary Creative"
              className={`${logoClass} hidden dark:block`}
            />
          </RouteLink>

          {/* Desktop nav - centered */}
          <ul className="absolute left-1/2 top-1/2 m-0 hidden -translate-x-1/2 -translate-y-1/2 list-none p-0 md:flex md:items-center md:gap-8 lg:gap-10">
            {navItems.map((item) => (
              <li key={item.label} className="flex items-center">
                <RouteLink to={item.href} className={navLinkClass}>
                  {item.label}
                </RouteLink>
              </li>
            ))}
          </ul>

          {/* Contact CTA - right */}
          <div className="hidden items-center gap-4 md:flex">
            <ThemeToggle />
            <MagneticLink href="/contact" variant="outlineCompact">
              Contact
            </MagneticLink>
          </div>

          {/* Mobile menu toggle */}
          <button
            type="button"
            className="relative flex h-11 w-11 flex-col items-center justify-center gap-1.5 md:hidden"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <motion.span
              className="h-0.5 w-6 bg-foreground"
              animate={{ rotate: isMobileMenuOpen ? 45 : 0, y: isMobileMenuOpen ? 4 : 0 }}
            />
            <motion.span
              className="h-0.5 w-6 bg-foreground"
              animate={{ opacity: isMobileMenuOpen ? 0 : 1 }}
            />
            <motion.span
              className="h-0.5 w-6 bg-foreground"
              animate={{ rotate: isMobileMenuOpen ? -45 : 0, y: isMobileMenuOpen ? -4 : 0 }}
            />
          </button>
        </nav>
      </motion.header>

      {/* Mobile menu */}
      <motion.div
        className="fixed inset-0 z-40 bg-background md:hidden"
        initial={{ opacity: 0, pointerEvents: 'none' }}
        animate={{
          opacity: isMobileMenuOpen ? 1 : 0,
          pointerEvents: isMobileMenuOpen ? 'auto' : 'none',
        }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex h-full flex-col items-start justify-center gap-6 px-6">
          {navItems.map((item, i) => (
            <motion.div
              key={item.label}
              initial={{ opacity: 0, x: -20 }}
              animate={{
                opacity: isMobileMenuOpen ? 1 : 0,
                x: isMobileMenuOpen ? 0 : -20,
              }}
              transition={{ delay: i * 0.08 }}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <RouteLink to={item.href} className="text-4xl font-serif text-foreground">
                {item.label}
              </RouteLink>
            </motion.div>
          ))}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{
              opacity: isMobileMenuOpen ? 1 : 0,
              x: isMobileMenuOpen ? 0 : -20,
            }}
            transition={{ delay: navItems.length * 0.08 }}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <MagneticLink href="/contact" variant="outline" className="mt-2">
              Contact
            </MagneticLink>
          </motion.div>
        </div>
      </motion.div>
    </>
  )
}
